import qs from 'querystring';

import { basename, extname } from './path';
import hasOwnProperty from './hasOwnProperty';
import { toResourceName } from '../../core/support/toResourceName';
import { DocumentTypes, ImageTypes } from '../../core/constants/MimeTypes';
import { getAppConfig } from '../../AppConfig';

import { FileDesc } from '../../types/shared/FileDesc';
import { Attachment } from '../../types/shared/FileUpload';

// Images larger than this size (either width or height) will be resized
const ImageSize = {
  thumbnail: 360,
  normal: 1200,
  large: 2048,
};

type SizeName = keyof typeof ImageSize;

type ImageOptions = {
  size: SizeName;
  fit?: 'contain' | 'cover';
};

// There are many more valid params for imgix, but we'll support a strict subset
// for this use case. For reference:
// - https://npm.im/imgix-url-params
// - https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/react-imgix/index.d.ts
type ImgixParams = {
  auto: 'compress';
  w: number;
  h: number;
  fm?: 'jpg' | 'png';
  fit?: 'min' | 'max';
};

const defaultImageOptions: ImageOptions = {
  size: 'normal',
};

function getCdnHost(): string | null {
  let { environment } = getAppConfig();
  return environment?.cdnHost ?? null;
}

export function isImage(file: FileDesc) {
  return hasOwnProperty(ImageTypes, file.type);
}

export function isDocument(file: FileDesc) {
  return hasOwnProperty(DocumentTypes, file.type);
}

function shouldOptimize(file: FileDesc, ext: string) {
  if (!isImage(file)) {
    return false;
  }
  // Don't attempt to optimize SVG files.
  if (ext === 'svg') {
    return false;
  }
  return true;
}

export function getDownloadURL(file: FileDesc) {
  let name = basename(file.name, extname(file.name)) || 'file';
  let path = '/' + file.id + '/' + toResourceName(name);
  let ext = DocumentTypes[file.type] || 'bin';
  let cdnHost = getCdnHost();
  if (cdnHost != null) {
    return `https://${cdnHost}/files${path}.${ext}?dl=1`;
  } else {
    return `/api/files${path}.${ext}?dl=1`;
  }
}

export function toPreviewURL(url: string) {
  let cdnHost = getCdnHost();
  if (cdnHost != null && url.startsWith('/api/files/thumbnails/')) {
    return url.replace('/api/files/', `https://${cdnHost}/`);
  } else {
    return url;
  }
}

export function getFileURL(file: FileDesc, imgOpts?: ImageOptions) {
  let name = basename(file.name, extname(file.name)) || 'file';
  let ext = DocumentTypes[file.type] || 'bin';
  let getPath = (folder: string) => {
    let useImgix = folder === 'images';
    let params = useImgix ? toImgixParams(imgOpts) : '';
    let resourceName = toResourceName(name);
    return `/${folder}/${file.id}/${resourceName}.${ext}` + params;
  };
  let cdnHost = getCdnHost();
  if (cdnHost != null) {
    let path =
      imgOpts || shouldOptimize(file, ext)
        ? getPath('images')
        : getPath('files');
    return `https://${cdnHost}${path}`;
  } else {
    return getPath('api/files');
  }
}

// TODO: Remove this. For some reason several parts of the code use Attachment
// instead of FileDesc.
export function toFile(attachment: Attachment): FileDesc {
  let { id, fileType: type, fileName: name, size } = attachment;
  return { id, name, size, type };
}

function toImgixParams(imgOpts: ImageOptions = defaultImageOptions) {
  let { size, fit = 'contain' } = imgOpts;
  let pixelSize = ImageSize[size];
  let params: ImgixParams = {
    auto: 'compress',
    w: pixelSize,
    h: pixelSize,
  };
  if (fit === 'contain') {
    // This will downsize (but never upsize) and respect the original aspect
    // ratio without cropping.
    params.fit = 'max';
  } else {
    // This will downsize (but never upsize) and crop the image to the requested
    // aspect ratio; e.g. produce a square profile pic.
    params.fit = 'min';
  }
  return '?' + qs.stringify(params);
}
